import { useCallback } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash2, CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  updateExperimentSchema,
  type UpdateExperimentFormValues,
} from "@/lib/validation/experiment-schema";
import { useUpdateExperiment } from "@/hooks/use-experiments";
import type { Experiment } from "@/types/experiment";
import { ExperimentStatus } from "@/types/enums";

const PLATFORM_OPTIONS = [
  { value: "web", label: "Web" },
  { value: "mobile", label: "Mobile" },
  { value: "desktop", label: "Desktop" },
] as const;

interface EditExperimentDialogProps {
  experiment: Experiment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditExperimentDialog({
  experiment,
  open,
  onOpenChange,
}: EditExperimentDialogProps) {
  const updateMutation = useUpdateExperiment();
  const isLocked =
    experiment?.status === ExperimentStatus.RUNNING ||
    experiment?.status === ExperimentStatus.PAUSED;

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UpdateExperimentFormValues>({
    resolver: zodResolver(updateExperimentSchema),
    defaultValues: {
      name: experiment?.name ?? "",
      description: experiment?.description ?? "",
      variants: experiment?.variants ?? [],
      targeting: {
        newUsersOnly: experiment?.targeting?.newUsersOnly ?? false,
        platforms: (experiment?.targeting?.platforms ?? []) as Array<"web" | "mobile" | "desktop">,
        startDate: experiment?.targeting?.startDate
          ? new Date(experiment.targeting.startDate)
          : undefined,
        endDate: experiment?.targeting?.endDate
          ? new Date(experiment.targeting.endDate)
          : undefined,
      },
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "variants",
  });

  const variants = watch("variants");
  const totalWeight =
    variants?.reduce((sum, v) => sum + (v.weight || 0), 0) ?? 0;

  const targeting = watch("targeting");
  const selectedPlatforms = targeting?.platforms ?? [];

  const togglePlatform = useCallback(
    (platform: "web" | "mobile" | "desktop") => {
      const current = selectedPlatforms;
      const next = current.includes(platform)
        ? current.filter((p) => p !== platform)
        : [...current, platform];
      setValue("targeting.platforms", next, { shouldValidate: true });
    },
    [selectedPlatforms, setValue],
  );

  const onSubmit = useCallback(
    (data: UpdateExperimentFormValues) => {
      if (!experiment) return;

      const cleanedTargeting = cleanTargeting(data.targeting);

      updateMutation.mutate(
        {
          key: experiment.key,
          name: data.name,
          description: data.description,
          variants: isLocked ? undefined : data.variants,
          targeting: isLocked ? undefined : cleanedTargeting,
        },
        {
          onSuccess: () => onOpenChange(false),
        },
      );
    },
    [experiment, updateMutation, onOpenChange, isLocked],
  );

  if (!experiment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>실험 수정</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>실험 Key</Label>
            <code className="block rounded bg-muted px-3 py-2 text-sm font-mono">
              {experiment.key}
            </code>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-name">실험명</Label>
            <Input id="edit-name" {...register("name")} />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">설명</Label>
            <Textarea
              id="edit-description"
              rows={2}
              {...register("description")}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Variants</Label>
              {isLocked ? (
                <span className="text-xs text-amber-600">
                  진행/일시정지 실험은 weight 수정 불가
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs ${totalWeight === 10000 ? "text-emerald-600" : "text-destructive"}`}
                  >
                    합계: {(totalWeight / 100).toFixed(1)}%
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => append({ key: "", weight: 0 })}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    추가
                  </Button>
                </div>
              )}
            </div>

            {fields.map((field, index) => (
              <div key={field.id} className="flex gap-2 items-start">
                <div className="flex-1">
                  <Input
                    placeholder="variant key"
                    disabled={isLocked}
                    {...register(`variants.${index}.key`)}
                  />
                  {!isLocked && errors.variants?.[index]?.key && (
                    <p className="text-xs text-destructive mt-1">
                      {errors.variants[index].key.message}
                    </p>
                  )}
                </div>
                <div className="w-24">
                  <Controller
                    name={`variants.${index}.weight`}
                    control={control}
                    render={({ field: f }) => (
                      <Input
                        type="number"
                        disabled={isLocked}
                        value={f.value}
                        onChange={(e) => f.onChange(Number(e.target.value) || 0)}
                      />
                    )}
                  />
                  {!isLocked && errors.variants?.[index]?.weight && (
                    <p className="text-xs text-destructive mt-1">
                      {errors.variants[index].weight.message}
                    </p>
                  )}
                </div>
                <div className="w-12 text-center text-xs text-muted-foreground pt-2">
                  {variants?.[index]?.weight
                    ? `${(variants[index].weight / 100).toFixed(1)}%`
                    : "0%"}
                </div>
                {!isLocked && fields.length > 2 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`variant ${index + 1} 삭제`}
                    title="variant 삭제"
                    onClick={() => remove(index)}
                    className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}

            {!isLocked && errors.variants?.root && (
              <p className="text-sm text-destructive">
                {errors.variants.root.message}
              </p>
            )}
            {!isLocked && errors.variants?.message && (
              <p className="text-sm text-destructive">
                {errors.variants.message}
              </p>
            )}
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">타겟팅 설정</Label>
              {isLocked && (
                <span className="text-xs text-amber-600">
                  진행/일시정지 실험은 타겟팅 수정 불가
                </span>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">실험 기간</Label>
              <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2">
                <span className="text-xs text-muted-foreground">시작일</span>
                <Controller
                  name="targeting.startDate"
                  control={control}
                  render={({ field }) => (
                    <DatePickerField
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="시작일 선택"
                      disabled={isLocked}
                    />
                  )}
                />
                <span className="text-xs text-muted-foreground">종료일</span>
                <Controller
                  name="targeting.endDate"
                  control={control}
                  render={({ field }) => (
                    <DatePickerField
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="종료일 선택"
                      disableBefore={targeting?.startDate}
                      disabled={isLocked}
                    />
                  )}
                />
              </div>
              {errors.targeting?.startDate && (
                <p className="text-xs text-destructive">
                  {errors.targeting.startDate.message}
                </p>
              )}
              {errors.targeting?.endDate && (
                <p className="text-xs text-destructive">
                  {errors.targeting.endDate.message}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs text-muted-foreground">신규 유저만</Label>
                <p className="text-xs text-muted-foreground">
                  시작일 이후 가입한 유저에게만 노출
                </p>
              </div>
              <Controller
                name="targeting.newUsersOnly"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isLocked}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">플랫폼 제한</Label>
              <div className="flex gap-2">
                {PLATFORM_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={selectedPlatforms.includes(opt.value) ? "default" : "outline"}
                    size="sm"
                    disabled={isLocked}
                    onClick={() => togglePlatform(opt.value)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                미선택 시 모든 플랫폼에 노출
              </p>
            </div>

            {/* TODO: 구독 상태별 필터 (무료/유료/학생) — SubscriptionStatus enum 연동 필요 */}
            {/* TODO: 가입 기간별 필터 (가입 후 N일 이내/이후) — userCreatedAt 기반 범위 조건 */}
            {/* TODO: 사용량 기반 필터 (작품 N개 이상 작성한 유저) */}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              취소
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "저장 중..." : "저장"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DatePickerField({
  value,
  onChange,
  placeholder,
  disableBefore,
  disabled,
}: {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder: string;
  disableBefore?: Date;
  disabled?: boolean;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
          )}
        >
          <CalendarIcon className="mr-2 h-3.5 w-3.5" />
          {value ? format(value, "yyyy.MM.dd") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          disabled={disableBefore ? (date) => date < disableBefore : undefined}
          weekStartsOn={0}
        />
      </PopoverContent>
    </Popover>
  );
}

function cleanTargeting(
  targeting: UpdateExperimentFormValues["targeting"],
) {
  if (!targeting) return undefined;

  const { newUsersOnly, platforms, startDate, endDate } = targeting;
  const hasAnyTargeting =
    newUsersOnly || (platforms && platforms.length > 0) || startDate || endDate;

  if (!hasAnyTargeting) return undefined;

  return {
    ...(newUsersOnly ? { newUsersOnly: true } : {}),
    ...(platforms && platforms.length > 0 ? { platforms } : {}),
    ...(startDate ? { startDate: startDate.toISOString() } : {}),
    ...(endDate ? { endDate: endDate.toISOString() } : {}),
  };
}
